import { ResizablePanel } from 'Components/ResizablePanel';
import { MobXProviderContext, observer } from 'mobx-react';
import { useUIStore } from 'Modules/ui/index.store';
import { useIsSecondaryMenuVisible } from 'Modules/ui/secondaryMenu/hooks/useIsSecondaryMenuVisible';
import { useIsSmallScreen } from 'Modules/ui/secondaryMenu/hooks/useIsSmallScreen';
import React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { cn } from '~/lib/utils';
import {
  CONVERSATIONS_PANEL_DEFAULT_WIDTH,
  CONVERSATIONS_PANEL_MAX_WIDTH,
  CONVERSATIONS_PANEL_MIN_WIDTH,
} from './constants';
import ConversationsPanelContent from './ConversationsPanelContent';
import { ConversationListContext } from './ConversationsPanelContent/ConversationListContext';
import ConversationsPanelHeader from './ConversationsPanelHeader';
import { ConversationsPanelProps } from './interfaces';
import { buildConversationsPanelWidthKey } from './utils';

const ConversationsPanel: React.FC<ConversationsPanelProps> = () => {
  const {
    person: { IsLoggedIn, loggedInPersonId },
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const isSecondaryMenuVisible = useIsSecondaryMenuVisible();
  const isSmallScreen = useIsSmallScreen();
  const setIsSecondaryMenuOpen = useUIStore(
    (state) => state.setIsSecondaryMenuOpen
  );

  const panelRef = React.useRef<HTMLDivElement>(null);

  if (!IsLoggedIn) {
    return null;
  }

  const userId = Number(loggedInPersonId);
  const storageKey = buildConversationsPanelWidthKey(userId);

  return (
    <ConversationListContext.Provider
      value={{ panelRef, isSecondaryMenuVisible }}
    >
      <ResizablePanel
        storageKey={storageKey}
        defaultWidth={CONVERSATIONS_PANEL_DEFAULT_WIDTH}
        minWidth={CONVERSATIONS_PANEL_MIN_WIDTH}
        maxWidth={CONVERSATIONS_PANEL_MAX_WIDTH}
        disabled={isSmallScreen}
        aria-label="Resize conversation list"
        className={cn(
          'h-[calc(100%-70px)] left-0 z-50 lg:relative lg:left-auto',
          {
            '!hidden': !isSecondaryMenuVisible,
          }
        )}
        key={storageKey}
      >
        <div
          ref={panelRef}
          id="conversations-panel"
          className="flex h-full w-full min-w-0 flex-col"
          onMouseLeave={() => {
            if (isSmallScreen) {
              setIsSecondaryMenuOpen(false);
            }
          }}
        >
          <ConversationsPanelHeader />
          <ConversationsPanelContent />
        </div>
      </ResizablePanel>
    </ConversationListContext.Provider>
  );
};

export default observer(ConversationsPanel);
